import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Chip,
  IconButton,
  Alert,
  Grid,
  Paper,
  Divider,
  Tooltip,
  InputAdornment
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useSystemConfig } from '../../hooks/useSystemConfig'

const NotificationForm = ({ initialData, onSubmit, onCancel, isSubmitting }) => {
  const [emailInput, setEmailInput] = useState('')
  const [cronError, setCronError] = useState('')
  
  // Get system configuration
  const { data: systemConfig } = useSystemConfig()
  const upcomingDays = systemConfig?.upcomingInvoicesDays || 7

  // Validation schema
  const validationSchema = Yup.object({
    notificationType: Yup.string().required('Tipo de notificación es requerido'),
    recipients: Yup.array()
      .of(Yup.string().email('Email inválido'))
      .min(1, 'Debe tener al menos un destinatario'),
    cronSchedule: Yup.string().required('Programación es requerida'),
    description: Yup.string(),
    active: Yup.boolean()
  })

  // Form configuration
  const formik = useFormik({
    initialValues: {
      notificationType: initialData?.notificationType || '',
      recipients: initialData?.recipients || [],
      cronSchedule: initialData?.cronSchedule || '',
      description: initialData?.description || '',
      active: initialData?.active ?? true
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values)
    }
  })

  // Notification types
  const notificationTypes = [
    {
      value: 'weekly_reminder',
      label: 'Recordatorio Semanal',
      description: `Notificación de facturas próximas a vencer en ${upcomingDays} días`,
      defaultCron: '0 9 * * 1',
      color: 'primary'
    },
    {
      value: 'overdue_alert',
      label: 'Alerta de Vencidas',
      description: 'Alerta diaria de facturas ya vencidas',
      defaultCron: '0 10 * * *',
      color: 'error'
    },
    {
      value: 'monthly_report',
      label: 'Reporte Mensual',
      description: 'Reporte mensual del estado de cuentas',
      defaultCron: '0 8 1 * *',
      color: 'info'
    },
    {
      value: 'payment_received',
      label: 'Pago Recibido',
      description: 'Notificación cuando se recibe un pago',
      defaultCron: '*/5 * * * *',
      color: 'success'
    },
    {
      value: 'new_invoice',
      label: 'Nueva Factura',
      description: 'Notificación cuando se crea una nueva factura',
      defaultCron: '*/10 * * * *',
      color: 'warning'
    }
  ]

  // Common cron schedules
  const commonSchedules = [
    { value: '0 9 * * 1', label: 'Lunes 9:00 AM' },
    { value: '0 10 * * *', label: 'Diario 10:00 AM' },
    { value: '0 8 1 * *', label: 'Mensual (día 1) 8:00 AM' },
    { value: '0 17 * * 5', label: 'Viernes 5:00 PM' },
    { value: '*/15 * * * *', label: 'Cada 15 minutos' },
    { value: '0 * * * *', label: 'Cada hora' },
    { value: '0 0 * * *', label: 'Diario medianoche' },
    { value: '0 12 * * *', label: 'Diario mediodía' }
  ]

  // Handle notification type change
  const handleNotificationTypeChange = (event) => {
    const selectedType = event.target.value
    formik.setFieldValue('notificationType', selectedType)

    // Set default cron schedule for the selected type
    const typeConfig = notificationTypes.find(type => type.value === selectedType)
    if (typeConfig && !formik.values.cronSchedule) {
      formik.setFieldValue('cronSchedule', typeConfig.defaultCron)
    }

    // Set default description
    if (typeConfig && !formik.values.description) {
      formik.setFieldValue('description', typeConfig.description)
    }
  }

  // Handle email addition
  const handleAddEmail = () => {
    if (emailInput && isValidEmail(emailInput)) {
      if (!formik.values.recipients.includes(emailInput)) {
        formik.setFieldValue('recipients', [...formik.values.recipients, emailInput])
        setEmailInput('')
      }
    }
  }

  // Handle email removal
  const handleRemoveEmail = (emailToRemove) => {
    formik.setFieldValue(
      'recipients',
      formik.values.recipients.filter(email => email !== emailToRemove)
    )
  }

  // Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Handle email input key press
  const handleEmailKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAddEmail()
    }
  }

  // Validate cron expression
  const validateCron = (cronExpression) => {
    if (!cronExpression) return ''

    // Basic cron validation (5 fields)
    const cronParts = cronExpression.trim().split(/\s+/)
    if (cronParts.length !== 5) {
      return 'La expresión cron debe tener 5 campos (minuto hora día mes día-semana)'
    }

    // Validate each part
    const [minute, hour, day, month, dayOfWeek] = cronParts

    // Basic range validation
    const isValidRange = (value, min, max) => {
      if (value === '*') return true
      if (value.includes('/')) {
        const [range, step] = value.split('/')
        return isValidRange(range, min, max) && !isNaN(step) && step > 0
      }
      if (value.includes('-')) {
        const [start, end] = value.split('-')
        return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end
      }
      if (value.includes(',')) {
        return value.split(',').every(v => isValidRange(v.trim(), min, max))
      }
      const num = parseInt(value)
      return !isNaN(num) && num >= min && num <= max
    }

    if (!isValidRange(minute, 0, 59)) return 'Minuto inválido (0-59)'
    if (!isValidRange(hour, 0, 23)) return 'Hora inválida (0-23)'
    if (!isValidRange(day, 1, 31)) return 'Día inválido (1-31)'
    if (!isValidRange(month, 1, 12)) return 'Mes inválido (1-12)'
    if (!isValidRange(dayOfWeek, 0, 6)) return 'Día de la semana inválido (0-6)'

    return ''
  }

  // Handle cron change
  const handleCronChange = (event) => {
    const value = event.target.value
    formik.setFieldValue('cronSchedule', value)
    setCronError(validateCron(value))
  }

  // Get selected type config
  const selectedTypeConfig = notificationTypes.find(
    type => type.value === formik.values.notificationType
  )

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        {/* Notification Type */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Tipo de Notificación</InputLabel>
            <Select
              name="notificationType"
              value={formik.values.notificationType}
              onChange={handleNotificationTypeChange}
              error={formik.touched.notificationType && Boolean(formik.errors.notificationType)}
            >
              {notificationTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Chip
                      label={type.label}
                      color={type.color}
                      size="small"
                      sx={{ mr: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {type.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {formik.touched.notificationType && formik.errors.notificationType && (
              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                {formik.errors.notificationType}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="description"
            label="Descripción"
            multiline
            rows={2}
            value={formik.values.description}
            onChange={formik.handleChange}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            placeholder="Descripción opcional de la configuración"
          />
        </Grid>

        {/* Recipients */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Destinatarios
          </Typography>
          
          {/* Email Input */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Ingresa un email y presiona Enter"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyPress={handleEmailKeyPress}
              error={emailInput && !isValidEmail(emailInput)}
              helperText={emailInput && !isValidEmail(emailInput) ? 'Email inválido' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleAddEmail}
                      disabled={!emailInput || !isValidEmail(emailInput)}
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Recipients List */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {formik.values.recipients.map((email, index) => (
              <Chip
                key={index}
                label={email}
                onDelete={() => handleRemoveEmail(email)}
                color="primary"
                variant="outlined"
                deleteIcon={<DeleteIcon />}
              />
            ))}
          </Box>

          {formik.touched.recipients && formik.errors.recipients && (
            <Typography variant="caption" color="error">
              {formik.errors.recipients}
            </Typography>
          )}
        </Grid>

        {/* Cron Schedule */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Programación
          </Typography>

          {/* Common Schedules */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Programaciones comunes:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {commonSchedules.map((schedule) => (
                <Chip
                  key={schedule.value}
                  label={schedule.label}
                  size="small"
                  variant={formik.values.cronSchedule === schedule.value ? 'filled' : 'outlined'}
                  onClick={() => {
                    formik.setFieldValue('cronSchedule', schedule.value)
                    setCronError('')
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          {/* Custom Cron Input */}
          <TextField
            fullWidth
            name="cronSchedule"
            label="Expresión Cron"
            value={formik.values.cronSchedule}
            onChange={handleCronChange}
            error={
              (formik.touched.cronSchedule && Boolean(formik.errors.cronSchedule)) ||
              Boolean(cronError)
            }
            helperText={
              cronError ||
              (formik.touched.cronSchedule && formik.errors.cronSchedule) ||
              "Formato: minuto hora día mes día-semana (ej: 0 9 * * 1 = Lunes 9:00 AM)"
            }
            placeholder="0 9 * * 1"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Formato cron: minuto(0-59) hora(0-23) día(1-31) mes(1-12) día-semana(0-6)">
                    <InfoIcon color="action" />
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />

          {/* Cron Validation Indicator */}
          {formik.values.cronSchedule && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {cronError ? (
                <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
              )}
              <Typography variant="caption" color={cronError ? 'error' : 'success'}>
                {cronError || 'Expresión cron válida'}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                name="active"
                checked={formik.values.active}
                onChange={formik.handleChange}
              />
            }
            label="Configuración activa"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Solo las configuraciones activas se ejecutarán automáticamente
          </Typography>
        </Grid>

        {/* Preview */}
        {selectedTypeConfig && formik.values.cronSchedule && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Vista Previa
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Tipo:</strong> {selectedTypeConfig.label}
                </Typography>
                <Typography variant="body2">
                  <strong>Destinatarios:</strong> {formik.values.recipients.length} email(s)
                </Typography>
                <Typography variant="body2">
                  <strong>Programación:</strong> {formik.values.cronSchedule}
                </Typography>
                <Typography variant="body2">
                  <strong>Estado:</strong> {formik.values.active ? 'Activa' : 'Inactiva'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Form Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || Boolean(cronError) || !formik.isValid}
        >
          {isSubmitting ? 'Guardando...' : initialData ? 'Actualizar' : 'Crear'}
        </Button>
      </Box>
    </Box>
  )
}

export default NotificationForm
